import posthog from 'posthog-js';
import { aliasRecorderUrl } from '../lib/posthogAssets';

// PostHog product analytics and session replay.
//
// Client-only, initialized from `Providers` once the browser is idle. Init and
// the exit listener move together, so deferring both keeps the ordering this
// module depends on intact.
//
// The token and host are public build-time values (deploy/frontend.env.production).
const projectToken: string = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const apiHost: string = import.meta.env.VITE_POSTHOG_HOST;

// Module-level, not component state: React's StrictMode runs effects twice in
// dev and `Providers` can remount, and neither init nor the exit listener is
// free to repeat.
let started = false;

/**
 * Flush the session recorder while there is still something to send it with.
 *
 * WebKit does not fire `beforeunload` when an in-app browser is dismissed, and
 * a visitor who reads without clicking never trips the recorder's flush timer
 * either - it stays unscheduled until the first interactive event. For a
 * passive visit arriving from a link in X, an exit flush is the only thing that
 * ever ships the replay, and production shows a fifth of those sessions
 * reaching PostHog as events with no recording attached.
 *
 * posthog-js does flush the recorder on `pagehide` as of 1.416.1, but it
 * registers that listener when recording starts, which is after remote config
 * and the recorder bundle have loaded - so it runs after posthog's own
 * `pagehide` handler has already drained the request queue with sendBeacon, and
 * the snapshot it flushes has nothing left to carry it out. Re-dispatching
 * `beforeunload` from a listener registered before `posthog.init` runs the same
 * flush early enough to make that drain, and releases the hold posthog-js keeps
 * on interaction-less recordings. The library's own later flush then finds an
 * empty buffer and does nothing.
 *
 * Only `pagehide`, deliberately. The lost sessions all shipped a `$pageleave`,
 * which posthog-js captures on `pagehide`, so that event demonstrably fires for
 * them. Flushing on `visibilitychange` instead would fire on ordinary tab
 * switches, where posthog's handler discards the whole buffer outright whenever
 * recording status is `BUFFERING` - which a sampling, trigger, or
 * minimum-duration setting in the PostHog dashboard would switch on with no
 * deploy at all.
 */
function flushRecorderOnExit(): void {
  window.addEventListener('pagehide', () => {
    window.dispatchEvent(new Event('beforeunload'));
  });
}

/**
 * Initialize PostHog once per document. Safe to call repeatedly, and safe to
 * call during SSR, where it does nothing.
 */
export function startPostHog(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  flushRecorderOnExit();
  posthog.init(projectToken, {
    api_host: apiHost,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-01-30',
    disable_surveys: true,
    // The recorder is served under a different file name; `lib/posthogAssets`
    // explains why and owns both ends of the rename.
    prepare_external_dependency_script: (script) => {
      script.src = aliasRecorderUrl(script.src);
      return script;
    },
  });
}
